import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Plane, Droplets, Scale } from "lucide-react";
import { Link } from "react-router-dom";
import { privateServices, serviceCategories, clinicalServices, bloodTestProfiles } from "@/data/privateServices";

// Import hero images
import infectionHero from "@/assets/private-services/infection-hero.jpg";
import injectionHero from "@/assets/private-services/injection-hero.jpg";
import earHero from "@/assets/private-services/ear-hero.jpg";
import healthHero from "@/assets/private-services/health-hero.jpg";
import bloodtestHero from "@/assets/private-services/bloodtest-hero.jpg";
import travelClinic from "@/assets/travel-clinic.jpg";
import phlebotomy from "@/assets/phlebotomy.jpg";
import weightLoss from "@/assets/weight-loss.jpg";

const heroImages: Record<string, string> = {
  'infection-hero': infectionHero,
  'injection-hero': injectionHero,
  'ear-hero': earHero,
  'health-hero': healthHero,
  'bloodtest-hero': bloodtestHero,
};

const clinicalImages: Record<string, string> = {
  'travel-clinic': travelClinic,
  'phlebotomy': phlebotomy,
  'weight-loss': weightLoss,
};

export const PrivateServicesTab = () => {
  return (
    <div className="space-y-16">
      {/* Travel Clinic Featured Card */}
      <Link to="/travel-clinic" className="block">
        <Card className="overflow-hidden hover:shadow-xl transition-all border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 group">
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
              <Plane className="w-10 h-10 text-primary" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold text-foreground mb-2">Travel Clinic & Vaccinations</h3>
              <p className="text-muted-foreground mb-4">
                Comprehensive travel vaccinations including Yellow Fever certification, Hepatitis, Typhoid, Rabies and destination-specific advice. Official Yellow Fever centre.
              </p>
              <Button variant="hero" className="group/btn">
                Explore Travel Clinic
                <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </Card>
      </Link>

      {/* Weight Loss Featured Card */}
      <Link to="/weight-loss-hull" className="block">
        <Card className="overflow-hidden hover:shadow-xl transition-all border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 group">
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
              <Scale className="w-10 h-10 text-primary" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold text-foreground mb-2">Weight Loss Treatment</h3>
              <p className="text-muted-foreground mb-4">
                Pharmacist-led weight management programme featuring Mounjaro® (tirzepatide) injections. Includes clinical assessment, eligibility screening, ongoing monitoring and lifestyle support.
              </p>
              <Button variant="hero" className="group/btn">
                Explore Weight Loss Options
                <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </Card>
      </Link>

      {/* Clinical Services */}
      <div className="space-y-6">
        <h3 className="text-2xl md:text-3xl font-bold text-foreground">Clinical Services</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clinicalServices.filter(s => s.id !== 'travel-clinic').map((service) => (
            <Card 
              key={service.id} 
              className="overflow-hidden hover:shadow-[var(--shadow-glow)] transition-all duration-300 group bg-gradient-to-b from-card to-background border-border"
            >
              {service.image && clinicalImages[service.image] && (
                <div className="h-40 overflow-hidden">
                  <img 
                    src={clinicalImages[service.image]} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              <div className="p-6">
                <h4 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {service.title}
                </h4>
                <p className="text-muted-foreground mb-4 line-clamp-2">
                  {service.shortDescription}
                </p>
                <Link to={`/services/${service.id}`} aria-label={`Learn more about ${service.title}`}>
                  <Button variant="outline" className="w-full group/btn">
                    <span>Learn more about {service.title}</span>
                    <ArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Why Choose Section */}
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 md:p-12">
        <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
          Why Choose Wilberforce Pharmacy?
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            'Trusted local pharmacy in Hull',
            'Qualified and experienced pharmacists',
            'Private consultation rooms',
            'No GP referral required',
            'Fast appointments and walk-in availability',
            'Confidential and discreet service'
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Services by Category - excluding bloodtest and bloodprofile as they have dedicated section */}
      {serviceCategories.filter(cat => cat.id !== 'bloodtest' && cat.id !== 'bloodprofile').map((category) => {
        const categoryServices = privateServices.filter(s => s.category === category.id);
        if (categoryServices.length === 0) return null;
        
        return (
          <div key={category.id} className="space-y-6">
            {/* Category Hero Image */}
            <div className="relative rounded-2xl overflow-hidden h-48 md:h-64">
              <img 
                src={heroImages[category.heroImage]} 
                alt={category.label}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/60 to-transparent" />
              <div className="absolute inset-0 flex items-center p-6 md:p-10">
                <div>
                  <span className="text-3xl md:text-4xl mb-2 block">{category.icon}</span>
                  <h3 className="text-2xl md:text-4xl font-bold text-foreground">
                    {category.label}
                  </h3>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryServices.map((service) => (
                <Card 
                  key={service.id} 
                  className="p-6 hover:shadow-[var(--shadow-glow)] transition-all duration-300 group bg-gradient-to-b from-card to-background border-border"
                >
                  <h4 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {service.title}
                  </h4>
                  <p className="text-muted-foreground mb-4 line-clamp-2">
                    {service.shortDescription}
                  </p>
                  <Link to={`/services/${service.id}`} aria-label={`Learn more about ${service.title}`}>
                    <Button variant="outline" className="w-full group/btn">
                      <span>Learn more about {service.title}</span>
                      <ArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {/* Private Blood Tests Featured Section */}
      <div className="space-y-6">
        {/* Blood Tests Hero */}
        <Link to="/private-blood-tests" className="block">
          <Card className="overflow-hidden hover:shadow-xl transition-all border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 group">
            <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <Droplets className="w-10 h-10 text-primary" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-foreground mb-2">Private Blood Tests & Profiles</h3>
                <p className="text-muted-foreground mb-4">
                  Comprehensive blood testing including {bloodTestProfiles.length} curated test profiles for diabetes, hormones, vitamins, fertility and more. Fast results with professional guidance.
                </p>
                <Button variant="hero" className="group/btn">
                  View All Blood Tests
                  <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </Card>
        </Link>

        {/* Blood Test Profiles Preview */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bloodTestProfiles.slice(0, 3).map((profile) => (
            <Card 
              key={profile.id} 
              className="p-6 hover:shadow-[var(--shadow-glow)] transition-all duration-300 group bg-gradient-to-b from-card to-background border-border"
            >
              <div className="flex justify-between items-start mb-3">
                <h4 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                  {profile.title}
                </h4>
                <span className="text-lg font-bold text-primary">{profile.price}</span>
              </div>
              <p className="text-muted-foreground mb-3 line-clamp-2 text-sm">
                {profile.shortDescription}
              </p>
              <div className="flex flex-wrap gap-1 mb-4">
                {profile.testsIncluded.slice(0, 2).map((test, idx) => (
                  <span key={idx} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {test}
                  </span>
                ))}
                {profile.testsIncluded.length > 2 && (
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                    +{profile.testsIncluded.length - 2} more
                  </span>
                )}
              </div>
              <Link to={`/private-blood-tests/profiles/${profile.id}`}>
                <Button variant="outline" className="w-full group/btn">
                  View Profile
                  <ArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </Card>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center">
          <Link to="/private-blood-tests">
            <Button variant="outline" size="lg" className="group/btn">
              View All {bloodTestProfiles.length} Blood Test Profiles
              <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>

      {/* What to Expect */}
      <div className="bg-muted/30 rounded-2xl p-8 md:p-12">
        <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
          What to Expect at Your Appointment
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { step: '1', title: 'Private Consultation', desc: "You'll meet with a pharmacist in a confidential setting." },
            { step: '2', title: 'Clinical Assessment', desc: 'We review symptoms, medical history and suitability.' },
            { step: '3', title: 'Treatment Where Appropriate', desc: 'Medication or service provided safely and professionally.' },
            { step: '4', title: 'Advice & Follow-Up', desc: 'Guidance on ongoing care and when to seek further help.' }
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">{item.step}</span>
              </div>
              <h4 className="font-semibold text-foreground mb-2">{item.title}</h4>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
